import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { tokens } from "@fluentui/react-components";
import {
    TextBold24Regular,
    TextItalic24Regular,
    TextUnderline24Regular,
    TextBulletList24Regular,
    TextNumberListLtr24Regular,
    Link24Regular,
    Image24Regular,
    PaintBrush24Regular,
    TextClearFormatting24Regular,
} from "@fluentui/react-icons";
import { I } from "../../redesign/kvpIcons";
import { sanitizeComposeHtml, INLINE_IMAGE_ALLOWED_TYPES, INLINE_IMAGE_MAX_BYTES } from "../composeSanitize";
import {
    EDITOR_DEFAULT_FONT,
    EDITOR_DEFAULT_FONT_STACK,
    EDITOR_DEFAULT_SIZE_PT,
    FONT_FAMILIES,
    FONT_SIZES,
    applyFontSize,
    escapeAttr,
    execCmd,
    insertHtmlAtCursor,
    isHtmlEmpty,
    makeRangeKeeper,
} from "./editorCommands";
import { behandleListenTaste } from "./listenTasten";
import {
    TextFormat,
    formatierungLoeschen,
    liesAnzeigeFormat,
    liesFormatAnAuswahl,
    listenpunkteAngleichen,
    wendeFormatAn,
} from "./formatUebertragen";
import { listenpunkteDerAuswahl, setzeListenpunktFormat } from "./listenFormat";
import { listenEbenenAngleichen } from "./listenEbenen";
import { findeListenpunkt } from "./autoListe";
import { fortsetzungsStart, listenStart, setzeListenStart } from "./listenStart";
import "./richtext.css";
import {
    RECHTSCHREIB_HILFE_TITEL,
    RechtschreibIcon,
    rechtschreibKnopfTitel,
    zeigeRechtschreibHilfe,
    zeigeRechtschreibHilfeAktiv,
} from "./RechtschreibHilfe";
import { useRechtschreibung } from "./rechtschreibung/useRechtschreibung";
import { DiktatKnopf } from "../../diktat/DiktatKnopf";
import { auswahlIn, inEditableEinfuegen } from "../../diktat/diktatEinfuegen";
import { TabLineal } from "./TabLineal";
import { absatzVon } from "./tabulator";
import {
    absaetzeDerAuswahl,
    einzugImEditor,
    htmlFuerAusgabe,
    htmlFuerVersand,
    imZitierten,
    stoppsAendern,
    stoppsVon,
    tabsAusrichten,
    tabsVorbereiten,
} from "./tabAusrichtung";
import { type TabArt, type Tabstopp, stoppEntfernen, stoppSetzen } from "./tabstopps";
import { alleDokumente, aktiveAuswahl, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * RichTextEditor — der gemeinsame WYSIWYG-Editor.
 *
 * Herausgelöst aus `ComposeMailWindow`, damit Verfassen-Fenster, Signaturen und
 * Vorlagen denselben Editor benutzen statt drei Varianten zu pflegen.
 *
 * ══ UNCONTROLLED ═══════════════════════════════════════════════════════════
 * Das `contentEditable` ist die Quelle der Wahrheit; React rendert nie hinein.
 * Der Inhalt wird ausschließlich über den imperativen Handle gelesen
 * (`getHtml()`), nie über einen State.
 *
 * Der Grund: Ein controlled contentEditable setzt bei jedem Re-Render das
 * `innerHTML` neu, und der Cursor springt dabei an den Anfang — der klassische
 * contentEditable-Bug. `onInput` ist deshalb bewusst nur ein Dirty-Signal und
 * nie eine Datenquelle. Wer speichert, ruft `getHtml()` und bekommt garantiert
 * den letzten Tastenanschlag.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Editor kennt die Mail-Domäne NICHT: Wie ein eingefügtes Bild transportiert
 * wird, entscheidet der Aufrufer über `onInsertImage`. Das Verfassen-Fenster
 * liefert eine Blob-URL mit `data-cid` (Anhang-Tracking), die Einstellungen
 * einen `data:`-URI (wird so gespeichert).
 */

/** Was `onInsertImage` zurückgibt: die Quelle plus optionale Zusatzattribute. */
export type InsertedImage = { src: string; attrs?: Record<string, string> };

export type RichTextEditorProps = {
    initialHtml?: string;
    placeholder?: string;
    ariaLabel?: string;
    /**
     * Icon-Satz der Werkzeugleiste. `"fluent"` (Default) hält das Verfassen-
     * Fenster optisch unverändert; `"kvp"` nutzt das hauseigene Icon-Set für die
     * Fluent-freien Flächen.
     */
    iconVariant?: "fluent" | "kvp";
    minHeight?: number;
    editorStyle?: React.CSSProperties;
    imageMaxBytes?: number;
    allowedImageTypes?: readonly string[];
    /** Bestimmt, wie ein eingefügtes Bild transportiert wird. Default: `data:`-URI. */
    onInsertImage?: (file: File) => Promise<InsertedImage | null>;
    onError?: (msg: string) => void;
    /** NUR Dirty-Signal — niemals als Datenquelle verwenden. */
    onInput?: () => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    /**
     * IME-Komposition (z. B. asiatische Eingabemethoden). Wer am DOM des Editors
     * operiert, muss währenddessen aussetzen — sonst bricht die Komposition ab.
     */
    onCompositionStart?: (e: React.CompositionEvent) => void;
    onCompositionEnd?: (e: React.CompositionEvent) => void;
    /** Zusätzliche Bedienelemente rechts in der Werkzeugleiste (z. B. Vorlagen). */
    toolbarExtra?: React.ReactNode;
    /**
     * T-243: Knopf „Rechtschreibprüfung – Hilfe" in der Werkzeugleiste
     * (Default an). Er erklärt, wie die Prüfung des Browsers eingeschaltet wird.
     */
    spellcheckHilfe?: boolean;
    /**
     * T-243: eigene Rechtschreib- und Grammatikprüfung (LanguageTool über das
     * Backend) mit Unterstreichungen und Vorschlägen per Klick. Default aus.
     * Wirkt nur, wenn der Dienst verfügbar ist und der Browser die CSS Custom
     * Highlight API kann — sonst bleibt die Prüfung des Browsers.
     */
    rechtschreibung?: boolean;
    /**
     * Lineal mit frei setzbaren Tabstopps wie in Outlook (Nachtrag T-346).
     * Default an; ein- und ausblenden lässt es sich über die Werkzeugleiste.
     */
    lineal?: boolean;
};

export type RichTextEditorHandle = {
    getHtml(): string;
    /**
     * Wie `getHtml`, dazu die im Editor gemessene Breite jedes Tabs — nur für
     * Senden und Entwurf (`htmlFuerVersand`, `tabsFuerVersand`).
     */
    getHtmlFuerVersand(): string;
    setHtml(html: string): void;
    insertHtml(html: string): void;
    getRoot(): HTMLElement | null;
    focus(): void;
    isEmpty(): boolean;
};

/** Liest eine Datei als vollständigen `data:`-URI. */
const fileToDataUri = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result ?? ""));
        r.onerror = () => reject(r.error ?? new Error("Datei konnte nicht gelesen werden"));
        r.readAsDataURL(file);
    });

/** Ob das Lineal eingeblendet ist — je Browser gemerkt, nur Bequemlichkeit. */
const LINEAL_SPEICHER = "kmvs.rte.lineal";
const liesLinealAn = (): boolean => {
    try {
        return window.localStorage.getItem(LINEAL_SPEICHER) !== "aus";
    } catch {
        return true;
    }
};
const merkeLinealAn = (an: boolean): void => {
    try {
        window.localStorage.setItem(LINEAL_SPEICHER, an ? "an" : "aus");
    } catch {
        /* privates Fenster o. Ä. — dann eben nur für diese Sitzung */
    }
};

/** Kleines Lineal für den Umschalter in der Werkzeugleiste. */
const LinealIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden>
        <rect x="2" y="6.5" width="16" height="7" rx="1.2" />
        <path d="M5 6.5v2.6M8 6.5v1.6M11 6.5v2.6M14 6.5v1.6" />
    </svg>
);

/**
 * Werkzeugleisten-Schaltfläche.
 *
 * Die eine bewusste Abweichung vom bisherigen Compose-Button: `onMouseDown` mit
 * `preventDefault`. Ohne das wandert der Fokus beim Klick auf den Button, die
 * Textauswahl im Editor geht verloren, und Fett/Kursiv wirken auf nichts.
 * Optisch identisch.
 */
const TBtn: React.FC<{
    icon: React.ReactNode;
    title: string;
    onClick: () => void;
    onDoubleClick?: () => void;
    active?: boolean;
    /** Nur für Umschalter (Pinsel): setzt `aria-pressed`. */
    pressed?: boolean;
}> = ({
    icon,
    title,
    onClick,
    onDoubleClick,
    active,
    pressed,
}) => (
    <button
        type="button"
        title={title}
        aria-pressed={pressed}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 30, height: 30, borderRadius: 4, border: "none",
            background: active ? tokens.colorNeutralBackground1Selected : "transparent",
            color: tokens.colorNeutralForeground1, cursor: "pointer",
            transition: "background 0.12s",
        }}
        onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = tokens.colorNeutralBackground1Hover; }}
        onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
    >
        {icon}
    </button>
);

const Sep = () => (
    <div style={{ width: 1, height: 20, background: tokens.colorNeutralStroke2, margin: "0 2px" }} />
);

const selectStyle: React.CSSProperties = {
    fontSize: 12,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: 4,
    padding: "2px 4px",
    background: "#fff",
    cursor: "pointer",
};

export const RichTextEditor = React.forwardRef<RichTextEditorHandle, RichTextEditorProps>(
    function RichTextEditor(
        {
            initialHtml = "",
            placeholder = "Schreiben Sie Ihre Nachricht hier...",
            ariaLabel,
            iconVariant = "fluent",
            minHeight,
            editorStyle,
            imageMaxBytes = INLINE_IMAGE_MAX_BYTES,
            allowedImageTypes = INLINE_IMAGE_ALLOWED_TYPES,
            onInsertImage,
            onError,
            onInput,
            onKeyDown,
            onCompositionStart,
            onCompositionEnd,
            toolbarExtra,
            spellcheckHilfe = true,
            rechtschreibung = false,
            lineal = true,
        },
        ref,
    ) {
        const bodyRef = useRef<HTMLDivElement>(null);
        const imageInputRef = useRef<HTMLInputElement>(null);
        const linkBtnRef = useRef<HTMLDivElement>(null);
        const keeper = useRef(makeRangeKeeper(() => bodyRef.current)).current;
        /** T-243: arbeitet nur von außen am DOM (Highlights), siehe rechtschreibung/. */
        const rs = useRechtschreibung(bodyRef, rechtschreibung);
        /** Cursor beim Start eines Diktats — dort landet der Text (wie getippt). */
        const diktatStelleRef = useRef<Range | null>(null);

        const [linkOpen, setLinkOpen] = useState(false);
        const [linkPos, setLinkPos] = useState<{ top: number; left: number } | null>(null);
        const [linkUrl, setLinkUrl] = useState("https://");

        /**
         * T-347: Rechtsklick in eine Nummerierung — „Nummerierung beginnen bei …"
         * und „Nummerierung fortsetzen" (wie im Kontextmenü von Outlook/Word).
         * `fortsetzenAb` ist die Nummer, mit der die vorige Liste weiterzählen
         * würde (`null`: es gibt keine).
         */
        const [listenMenue, setListenMenue] = useState<{
            ol: HTMLElement;
            top: number;
            left: number;
            fortsetzenAb: number | null;
        } | null>(null);
        const [startEingabe, setStartEingabe] = useState("1");

        /**
         * Pinsel „Format übertragen": gemerktes Format der Quelle. `dauerhaft`
         * (Doppelklick) lässt den Pinsel nach dem Anwenden aktiv — wie in Word.
         */
        const [pinsel, setPinsel] = useState<{ format: TextFormat; dauerhaft: boolean } | null>(null);
        const pinselFormatRef = useRef<TextFormat | null>(null);
        /** Begann die Maus-Markierung im Editor? Nur dann wendet `mouseup` den Pinsel an. */
        const mausImEditorRef = useRef(false);

        /** Schrift/Größe an der Cursorposition — für die Anzeige in den Dropdowns. */
        const [aktSchrift, setAktSchrift] = useState(EDITOR_DEFAULT_FONT);
        const [aktGroesse, setAktGroesse] = useState(EDITOR_DEFAULT_SIZE_PT);
        /**
         * Größe, die ohne Markierung gewählt wurde: Der Browser merkt sie sich
         * als `<font size="7">` (Marker von `applyFontSize`) und legt sie erst
         * beim Tippen an — dann ist der Umschreib-Schritt längst vorbei, und es
         * bliebe Größe 7 (= 36 pt) statt der gewählten. Beim nächsten `input`
         * wird das nachgeholt.
         */
        const ausstehendeGroesseRef = useRef<number | null>(null);
        /**
         * Ebenso die ohne Markierung gewählte Schrift. Der Browser hält beides
         * nur unsichtbar bereit — ohne diese Merker sprängen die Dropdowns beim
         * nächsten `selectionchange` auf die Schrift am Cursor zurück, und eine
         * danach angelegte Liste bekäme nicht die eingestellte Schrift.
         */
        const ausstehendeSchriftRef = useRef<string | null>(null);
        const ausstehendVergessen = useCallback(() => {
            ausstehendeGroesseRef.current = null;
            ausstehendeSchriftRef.current = null;
        }, []);

        /**
         * Lineal mit Tabstopps (Nachtrag T-346). `linealStand` sind die Stopps
         * des Absatzes am Cursor (`null`: Cursor nicht im Text), `linealMass`
         * Breite und linker Rand der Schreibfläche — daran richtet sich die
         * Skala aus.
         */
        const [linealAn, setLinealAn] = useState(liesLinealAn);
        const [tabArt, setTabArt] = useState<TabArt>("links");
        const [linealStand, setLinealStand] = useState<{ stopps: Tabstopp[] | null; einzugPt: number }>({
            stopps: null,
            einzugPt: 0,
        });
        const [linealMass, setLinealMass] = useState({ breite: 0, versatz: 20 });
        /** Während einer IME-Komposition fasst die Ausrichtung das DOM nicht an. */
        const komponiertRef = useRef(false);
        const ausrichtenRef = useRef<number | null>(null);

        // Inhalt EINMAL beim Mounten setzen. React rendert nie in das
        // contentEditable hinein — sonst springt der Cursor.
        useEffect(() => {
            // Tabs/Tabstopps vorbereiten (Outlook-Format, eine Hülle je Tab), siehe tabAusrichtung.ts.
            if (bodyRef.current) bodyRef.current.innerHTML = tabsVorbereiten(initialHtml || "");
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useImperativeHandle(
            ref,
            (): RichTextEditorHandle => ({
                // Tabstopps gehen als `tab-stops` hinaus (Outlook), siehe tabAusrichtung.ts.
                getHtml: () => (bodyRef.current ? htmlFuerAusgabe(bodyRef.current) : ""),
                getHtmlFuerVersand: () => (bodyRef.current ? htmlFuerVersand(bodyRef.current) : ""),
                setHtml: (html: string) => {
                    ausstehendeGroesseRef.current = null;
                    ausstehendeSchriftRef.current = null;
                    if (bodyRef.current) bodyRef.current.innerHTML = tabsVorbereiten(html ?? "");
                },
                // Vorlagen und „Signatur einfügen": `insertHTML` verwürfe `tab-stops`.
                insertHtml: (html: string) => insertHtmlAtCursor(bodyRef.current, tabsVorbereiten(html)),
                getRoot: () => bodyRef.current,
                focus: () => bodyRef.current?.focus(),
                isEmpty: () => isHtmlEmpty(bodyRef.current?.innerHTML ?? ""),
            }),
            [],
        );

        /**
         * Listen entstehen und enden beim Tippen (Word-/Outlook-Verhalten):
         * „- " + Leertaste, Tab/Shift+Tab für die Ebene, Enter auf leerem
         * Punkt verlässt die Liste. Die DOM-Arbeit liegt seit dem 14.09.2026
         * in `listenTasten.ts` — dieselbe Funktion bedient die Termin-
         * Notizen-Seite und die V4-Notizblöcke; die Regeln (WANN) stehen
         * weiterhin pur in `autoListe.ts`.
         */
        const listenTaste = useCallback(
            (e: React.KeyboardEvent): boolean => {
                // T-346: Tab bleibt im Mail-Text (Einzug bzw. Tabulator) und
                // springt nicht mehr ins nächste Feld — wie in Outlook.
                const erledigt = behandleListenTaste(bodyRef.current, e, onInput, { tabImText: true });
                if (erledigt) listenEbenenAngleichen(bodyRef.current);
                return erledigt;
            },
            [onInput],
        );

        const icons =
            iconVariant === "kvp"
                ? {
                      bold: I.bold, italic: I.italic, underline: I.underline,
                      brush: I.brush, clearFormat: I.clearFormat,
                      listUl: I.listUl, listOl: I.listOl, link: I.link, image: I.image,
                  }
                : {
                      bold: <TextBold24Regular style={{ fontSize: 18 }} />,
                      italic: <TextItalic24Regular style={{ fontSize: 18 }} />,
                      underline: <TextUnderline24Regular style={{ fontSize: 18 }} />,
                      brush: <PaintBrush24Regular style={{ fontSize: 18 }} />,
                      clearFormat: <TextClearFormatting24Regular style={{ fontSize: 18 }} />,
                      listUl: <TextBulletList24Regular style={{ fontSize: 18 }} />,
                      listOl: <TextNumberListLtr24Regular style={{ fontSize: 18 }} />,
                      link: <Link24Regular style={{ fontSize: 18 }} />,
                      image: <Image24Regular style={{ fontSize: 18 }} />,
                  };

        /** Fügt ein Bild ein — Transport bestimmt der Aufrufer. */
        const handleFiles = useCallback(
            async (files: File[]) => {
                for (const file of files) {
                    if (!(allowedImageTypes as readonly string[]).includes(file.type)) {
                        onError?.(`„${file.name}": nur PNG, JPG, GIF oder WEBP als Inline-Bild (SVG nicht erlaubt).`);
                        continue;
                    }
                    if (file.size > imageMaxBytes) {
                        onError?.(`„${file.name}" ist zu groß (max. ${Math.round(imageMaxBytes / 1024)} KB pro Bild).`);
                        continue;
                    }
                    try {
                        const inserted = onInsertImage
                            ? await onInsertImage(file)
                            : { src: await fileToDataUri(file) };
                        if (!inserted) continue;
                        const attrsStr = Object.entries(inserted.attrs ?? {})
                            .map(([k, v]) => `${k}="${escapeAttr(v)}"`)
                            .join(" ");
                        insertHtmlAtCursor(
                            bodyRef.current,
                            `<img src="${inserted.src}"${attrsStr ? " " + attrsStr : ""} style="max-width:100%;height:auto;" alt="${escapeAttr(file.name)}">`,
                        );
                    } catch {
                        onError?.(`„${file.name}" konnte nicht eingelesen werden.`);
                    }
                }
            },
            [allowedImageTypes, imageMaxBytes, onError, onInsertImage],
        );

        /**
         * Einfügen aus der Zwischenablage.
         *
         * Bilder laufen durch dieselbe Prüfung wie der Datei-Dialog — vorher
         * landeten sie ungeprüft als roher `data:`-URI im DOM. HTML wird
         * sanitisiert eingefügt, damit kein Fremd-Markup mit Skripten oder
         * Tracking-Pixeln in die Mail gerät.
         */
        const handlePaste = useCallback(
            (e: React.ClipboardEvent) => {
                const files = Array.from(e.clipboardData?.files ?? []);
                if (files.length) {
                    e.preventDefault();
                    void handleFiles(files);
                    return;
                }
                const html = e.clipboardData?.getData("text/html");
                if (html) {
                    e.preventDefault();
                    // Tabs und Tabstopps aus Outlook/Word überleben das Einfügen nur umgebaut.
                    insertHtmlAtCursor(bodyRef.current, tabsVorbereiten(sanitizeComposeHtml(html)));
                    listenEbenenAngleichen(bodyRef.current);
                }
            },
            [handleFiles],
        );

        const handleDrop = useCallback(
            (e: React.DragEvent) => {
                const files = Array.from(e.dataTransfer?.files ?? []).filter((f) => f.type.startsWith("image/"));
                if (!files.length) return;
                e.preventDefault();
                void handleFiles(files);
            },
            [handleFiles],
        );

        /**
         * Die Dropdowns zeigen die Schrift an der Cursorposition — so fällt eine
         * eingefügte Fremdschrift (z. B. Calibri aus Outlook) auf, ohne dass man
         * den Code lesen muss.
         */
        const anzeigeAktualisieren = useCallback(() => {
            const root = bodyRef.current;
            if (!root) return;
            const f = liesAnzeigeFormat(root);
            if (!f) return;
            const ohneMarkierung = !!aktiveAuswahl()?.isCollapsed;
            setAktSchrift((ohneMarkierung && ausstehendeSchriftRef.current) || f.fontFamily);
            setAktGroesse((ohneMarkierung && ausstehendeGroesseRef.current) || f.sizePt);
        }, []);
        useEffect(() => {
            alleDokumente.addEventListener("selectionchange", anzeigeAktualisieren);
            return () => alleDokumente.removeEventListener("selectionchange", anzeigeAktualisieren);
        }, [anzeigeAktualisieren]);

        /** Das Lineal zeigt die Stopps des Absatzes, in dem der Cursor steht. */
        const linealLesen = useCallback(() => {
            const root = bodyRef.current;
            if (!root) return;
            const sel = aktiveAuswahl();
            const r = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
            let stand: { stopps: Tabstopp[] | null; einzugPt: number } = { stopps: null, einzugPt: 0 };
            if (r && root.contains(r.startContainer) && !imZitierten(root, r.startContainer)) {
                const absatz = absatzVon(root, r.startContainer);
                if (absatz !== null) {
                    const einzug = absatz === "root" ? 0 : einzugImEditor(root, absatz);
                    stand = { stopps: stoppsVon(absatz), einzugPt: Math.round(einzug * 10) / 10 };
                }
            }
            setLinealStand((alt) => (JSON.stringify(alt) === JSON.stringify(stand) ? alt : stand));
        }, []);
        useEffect(() => {
            if (!lineal) return;
            alleDokumente.addEventListener("selectionchange", linealLesen);
            return () => alleDokumente.removeEventListener("selectionchange", linealLesen);
        }, [lineal, linealLesen]);

        /**
         * Richtet die Tabs gleich nach jeder Änderung aus (`tabAusrichtung.ts`)
         * — am Text, an der Schrift oder an den Stopps, auch für Signatur und
         * Vorlage, die von außen eingesetzt werden.
         *
         * Zwei Läufe: Der zweite misst Rechts- und Zentriert-Stopps nach, falls
         * der Text dahinter im ersten noch umbrach. Danach verwirft
         * `takeRecords()` die Änderungen, die das Ausrichten selbst gemacht
         * hat — es stößt sich so nie selbst wieder an. Ohne diese Bremse lief
         * es in einem Grenzfall (Tab am rechten Rand) endlos.
         */
        const beobachterRef = useRef<MutationObserver | null>(null);
        const planeAusrichten = useCallback(() => {
            if (ausrichtenRef.current != null) return;
            ausrichtenRef.current = window.setTimeout(() => {
                ausrichtenRef.current = null;
                const root = bodyRef.current;
                if (!root || komponiertRef.current) return;
                tabsAusrichten(root);
                tabsAusrichten(root);
                beobachterRef.current?.takeRecords();
            }, 0);
        }, []);
        useEffect(() => {
            const root = bodyRef.current;
            if (!root || typeof MutationObserver === "undefined") return;
            const beobachter = new MutationObserver(planeAusrichten);
            beobachterRef.current = beobachter;
            beobachter.observe(root, {
                subtree: true,
                childList: true,
                characterData: true,
                attributes: true,
                attributeFilter: ["style", "class", "size", "face", "data-tabstopps"],
            });
            planeAusrichten();
            return () => {
                beobachter.disconnect();
                beobachterRef.current = null;
                if (ausrichtenRef.current != null) window.clearTimeout(ausrichtenRef.current);
                ausrichtenRef.current = null;
            };
        }, [planeAusrichten]);

        /** Breite und linker Rand der Schreibfläche — daran hängt die Skala des Lineals. */
        const linealMessen = useCallback(() => {
            const root = bodyRef.current;
            if (!root) return;
            const cs = window.getComputedStyle(root);
            const links = parseFloat(cs.paddingLeft) || 0;
            const rechts = parseFloat(cs.paddingRight) || 0;
            const breite = Math.max(0, root.clientWidth - links - rechts);
            const versatz = root.clientLeft + links;
            setLinealMass((alt) => (alt.breite === breite && alt.versatz === versatz ? alt : { breite, versatz }));
        }, []);
        useEffect(() => {
            const root = bodyRef.current;
            if (!root) return;
            linealMessen();
            if (typeof ResizeObserver === "undefined") return;
            // Breiter oder schmaler: Zeilen brechen anders um, die Tabs auch.
            const ro = new ResizeObserver(() => {
                linealMessen();
                planeAusrichten();
            });
            ro.observe(root);
            return () => ro.disconnect();
        }, [linealMessen, planeAusrichten]);

        /** Ändert die Stopps aller Absätze, die die Auswahl berührt — wie in Word. */
        const stoppsBearbeiten = useCallback(
            (aendern: (alt: Tabstopp[]) => Tabstopp[]) => {
                const root = bodyRef.current;
                if (!root) return;
                const absaetze = absaetzeDerAuswahl(root, true);
                if (!absaetze.length) return;
                stoppsAendern(absaetze, aendern);
                tabsAusrichten(root);
                linealLesen();
                onInput?.();
            },
            [linealLesen, onInput],
        );

        /**
         * Neue Liste: Die Nummer bzw. der Punkt soll Schrift und Größe des Textes
         * tragen — bei einem leeren Punkt die eingestellte (Dropdowns).
         */
        const listeAnlegen = useCallback(
            (cmd: "insertUnorderedList" | "insertOrderedList") => {
                const vorgabe = {
                    fontFamily: ausstehendeSchriftRef.current ?? aktSchrift,
                    sizePt: ausstehendeGroesseRef.current ?? aktGroesse,
                };
                execCmd(bodyRef.current, cmd);
                listenpunkteAngleichen(bodyRef.current, vorgabe);
                listenEbenenAngleichen(bodyRef.current);
                onInput?.();
            },
            [aktSchrift, aktGroesse, onInput],
        );

        /** Holt eine ohne Markierung gewählte Größe nach (siehe ausstehendeGroesseRef). */
        const handleInput = useCallback(() => {
            const pt = ausstehendeGroesseRef.current;
            const root = bodyRef.current;
            if (pt != null && root) {
                const marker = root.querySelectorAll<HTMLElement>('font[size="7"]');
                marker.forEach((f) => {
                    f.removeAttribute("size");
                    f.style.fontSize = `${pt}pt`;
                });
                if (marker.length) ausstehendeGroesseRef.current = null;
            }
            // Getippt ist getippt: Ab jetzt steht die Schrift im Text.
            if (root && aktiveAuswahl()?.isCollapsed) {
                ausstehendeSchriftRef.current = null;
            }
            // T-346: Listen, die der Browser selbst umbaut (Enter, Entf über
            // Listengrenzen …), bekommen wieder das Zeichen ihrer Ebene.
            if (root?.querySelector("ol, ul")) listenEbenenAngleichen(root);
            onInput?.();
        }, [onInput]);

        /** Wendet den Pinsel auf die aktuelle Markierung an, falls es eine im Editor gibt. */
        const pinselAnwenden = useCallback(() => {
            const root = bodyRef.current;
            if (!pinsel || !root) return;
            const sel = aktiveAuswahl();
            if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
            const r = sel.getRangeAt(0);
            if (!root.contains(r.commonAncestorContainer)) return;
            wendeFormatAn(root, pinsel.format);
            // Die Markierung bleibt oft gleich — dann kommt kein selectionchange,
            // und die Dropdowns zeigten weiter die alte Schrift.
            anzeigeAktualisieren();
            onInput?.();
            if (!pinsel.dauerhaft) setPinsel(null);
        }, [pinsel, onInput, anzeigeAktualisieren]);

        // Die Markierung kann auch außerhalb der Schreibfläche enden (Ziehen über
        // den Rand) — deshalb `mouseup` am Dokument, solange der Pinsel aktiv ist.
        useEffect(() => {
            if (!pinsel) return;
            mausImEditorRef.current = false;
            const onDown = (e: MouseEvent) => {
                mausImEditorRef.current = !!bodyRef.current?.contains(e.target as Node);
            };
            const onUp = () => {
                if (!mausImEditorRef.current) return;
                mausImEditorRef.current = false;
                pinselAnwenden();
            };
            alleDokumente.addEventListener("mousedown", onDown);
            alleDokumente.addEventListener("mouseup", onUp);
            return () => {
                alleDokumente.removeEventListener("mousedown", onDown);
                alleDokumente.removeEventListener("mouseup", onUp);
            };
        }, [pinsel, pinselAnwenden]);

        const pinselKlick = useCallback(() => {
            const root = bodyRef.current;
            if (!root) return;
            if (pinsel) {
                setPinsel(null);
                return;
            }
            const format = liesFormatAnAuswahl(root);
            pinselFormatRef.current = format;
            setPinsel({ format, dauerhaft: false });
        }, [pinsel]);

        const pinselDoppelklick = useCallback(() => {
            const root = bodyRef.current;
            if (!root) return;
            const format = pinselFormatRef.current ?? liesFormatAnAuswahl(root);
            pinselFormatRef.current = format;
            setPinsel({ format, dauerhaft: true });
        }, []);

        const openLinkPopover = useCallback(() => {
            keeper.save();
            const r = linkBtnRef.current?.getBoundingClientRect();
            if (r) setLinkPos({ top: r.bottom + 4, left: r.left });
            setLinkUrl("https://");
            setLinkOpen(true);
        }, [keeper]);

        const applyLink = useCallback(() => {
            const url = linkUrl.trim();
            setLinkOpen(false);
            if (!url || url === "https://") return;
            keeper.restore();
            execCmd(bodyRef.current, "createLink", url);
        }, [keeper, linkUrl]);

        /** Rechtsklick in eine Nummerierung öffnet das Listen-Menü (T-347). */
        const handleContextMenu = useCallback((e: React.MouseEvent) => {
            const root = bodyRef.current;
            // Umschalt+Rechtsklick: das Browser-Menü (Rechtschreibvorschläge).
            if (!root || e.shiftKey) return;
            const li = findeListenpunkt(e.target as Node, root);
            const ol = li?.parentElement;
            if (!li || !ol || ol.tagName !== "OL") return;
            e.preventDefault();
            setStartEingabe(String(listenStart(ol)));
            setListenMenue({
                ol,
                // Am Fensterrand nach innen klappen, statt abgeschnitten zu werden.
                top: Math.max(4, Math.min(e.clientY + 2, aktivesFenster().innerHeight - 170)),
                left: Math.max(4, Math.min(e.clientX + 2, aktivesFenster().innerWidth - 330)),
                fortsetzenAb: fortsetzungsStart(root, ol),
            });
        }, []);

        const listenMenueSchliessen = useCallback(() => {
            setListenMenue(null);
            bodyRef.current?.focus();
        }, []);

        const startUebernehmen = useCallback(() => {
            if (!listenMenue) return;
            const n = parseInt(startEingabe, 10);
            if (!Number.isFinite(n) || n < 1) return;
            setzeListenStart(listenMenue.ol, n);
            listenMenueSchliessen();
            onInput?.();
        }, [listenMenue, startEingabe, listenMenueSchliessen, onInput]);

        const fortsetzen = useCallback(() => {
            if (!listenMenue || listenMenue.fortsetzenAb == null) return;
            setzeListenStart(listenMenue.ol, listenMenue.fortsetzenAb);
            listenMenueSchliessen();
            onInput?.();
        }, [listenMenue, listenMenueSchliessen, onInput]);

        const startGueltig = /^\d{1,4}$/.test(startEingabe.trim()) && parseInt(startEingabe, 10) >= 1;

        return (
            <div className="rte">
                {/* Werkzeugleiste */}
                <div className="rte-toolbar" style={{
                    display: "flex", alignItems: "center", gap: 2,
                    padding: "4px 10px",
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    background: tokens.colorNeutralBackground2,
                    flexShrink: 0, flexWrap: "wrap",
                }}>
                    <select
                        value={aktSchrift}
                        onMouseDown={() => keeper.save()}
                        onChange={e => {
                            setAktSchrift(e.target.value);
                            keeper.restore();
                            // Listenpunkte vorher bestimmen — der Befehl verschiebt die Auswahl.
                            const lis = listenpunkteDerAuswahl(bodyRef.current);
                            const ohneMarkierung = !!aktiveAuswahl()?.isCollapsed;
                            execCmd(bodyRef.current, "fontName", e.target.value);
                            setzeListenpunktFormat(lis, { fontFamily: e.target.value });
                            ausstehendeSchriftRef.current = ohneMarkierung ? e.target.value : null;
                            if (lis.length) onInput?.();
                        }}
                        style={selectStyle}
                    >
                        {FONT_FAMILIES.map(f => <option key={f}>{f}</option>)}
                        {/* Fremdschrift (z. B. aus Outlook eingefügt): nur anzeigen, nicht wählbar. */}
                        {!FONT_FAMILIES.includes(aktSchrift) && <option disabled>{aktSchrift}</option>}
                    </select>
                    <select
                        value={String(aktGroesse)}
                        onMouseDown={() => keeper.save()}
                        onChange={e => {
                            const pt = Number(e.target.value);
                            setAktGroesse(pt);
                            keeper.restore();
                            const lis = listenpunkteDerAuswahl(bodyRef.current);
                            const ohneMarkierung = !!aktiveAuswahl()?.isCollapsed;
                            // Vorher leeren: execCommand löst selbst ein `input`
                            // aus, und ein alter Merker schriebe dabei die
                            // frischen Marker auf die alte Größe um.
                            ausstehendeGroesseRef.current = null;
                            applyFontSize(bodyRef.current, pt);
                            setzeListenpunktFormat(lis, { sizePt: pt });
                            ausstehendeGroesseRef.current = ohneMarkierung ? pt : null;
                            if (lis.length) onInput?.();
                        }}
                        style={{ ...selectStyle, width: 46 }}
                    >
                        {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                        {!FONT_SIZES.includes(aktGroesse) && (
                            <option disabled value={aktGroesse}>{aktGroesse}</option>
                        )}
                    </select>
                    <Sep />
                    <TBtn icon={icons.bold} title="Fett (Strg+B)" onClick={() => execCmd(bodyRef.current, "bold")} />
                    <TBtn icon={icons.italic} title="Kursiv (Strg+I)" onClick={() => execCmd(bodyRef.current, "italic")} />
                    <TBtn icon={icons.underline} title="Unterstrichen (Strg+U)" onClick={() => execCmd(bodyRef.current, "underline")} />
                    <TBtn
                        icon={icons.brush}
                        title="Format übertragen (Doppelklick: mehrfach)"
                        onClick={pinselKlick}
                        onDoubleClick={pinselDoppelklick}
                        active={!!pinsel}
                        pressed={!!pinsel}
                    />
                    <TBtn
                        icon={icons.clearFormat}
                        title="Formatierung löschen (Standardschrift)"
                        onClick={() => { formatierungLoeschen(bodyRef.current); anzeigeAktualisieren(); onInput?.(); }}
                    />
                    <Sep />
                    <TBtn icon={icons.listUl} title="Aufzählung" onClick={() => listeAnlegen("insertUnorderedList")} />
                    <TBtn icon={icons.listOl} title="Nummerierung (bei anderer Startnummer: „2. “ tippen oder Rechtsklick in die Liste)" onClick={() => listeAnlegen("insertOrderedList")} />
                    {lineal && (
                        <TBtn
                            icon={<LinealIcon />}
                            title={linealAn ? "Lineal ausblenden" : "Lineal mit Tabstopps einblenden"}
                            onClick={() => {
                                const neu = !linealAn;
                                setLinealAn(neu);
                                merkeLinealAn(neu);
                            }}
                            active={linealAn}
                            pressed={linealAn}
                        />
                    )}
                    <Sep />
                    <div ref={linkBtnRef} style={{ display: "flex" }}>
                        <TBtn icon={icons.link} title="Link einfügen" onClick={openLinkPopover} />
                    </div>
                    <TBtn icon={icons.image} title="Bild einfügen" onClick={() => imageInputRef.current?.click()} />
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept={(allowedImageTypes as readonly string[]).join(",")}
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => {
                            const files = Array.from(e.target.files ?? []);
                            e.target.value = "";
                            if (files.length) void handleFiles(files);
                        }}
                    />
                    <DiktatKnopf
                        onStart={() => {
                            diktatStelleRef.current = auswahlIn(bodyRef.current);
                        }}
                        onText={(text) => {
                            const el = bodyRef.current;
                            const gemerkt = diktatStelleRef.current;
                            diktatStelleRef.current = null;
                            if (!el) return;
                            // Ohne gemerkte Stelle an den ANFANG — am Ende stünden
                            // Signatur und zitierte Mail davor.
                            inEditableEinfuegen(el, gemerkt, text, () => {
                                const r = el.ownerDocument.createRange();
                                r.selectNodeContents(el);
                                r.collapse(true);
                                return r;
                            });
                        }}
                    />
                    {toolbarExtra}
                    {spellcheckHilfe && (
                        <>
                            <Sep />
                            <div style={{ position: "relative", display: "flex" }}>
                                <TBtn
                                    icon={<RechtschreibIcon />}
                                    title={rs.aktiv ? rechtschreibKnopfTitel(rs.anzahl) : RECHTSCHREIB_HILFE_TITEL}
                                    onClick={() => {
                                        if (!rs.aktiv) void zeigeRechtschreibHilfe();
                                        else if (!rs.zumErsten()) void zeigeRechtschreibHilfeAktiv();
                                    }}
                                />
                                {rs.aktiv && rs.anzahl > 0 && (
                                    <span className="rte-rs-zahl" aria-hidden>{rs.anzahl > 99 ? "99+" : rs.anzahl}</span>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {lineal && linealAn && linealMass.breite > 0 && (
                    <TabLineal
                        breitePx={linealMass.breite}
                        versatzPx={linealMass.versatz}
                        stopps={linealStand.stopps}
                        einzugPt={linealStand.einzugPt}
                        art={tabArt}
                        onArt={setTabArt}
                        onSetzen={(stopp) => stoppsBearbeiten((alt) => stoppSetzen(alt, stopp))}
                        onVerschieben={(altPt, neu) =>
                            stoppsBearbeiten((alt) => stoppSetzen(stoppEntfernen(alt, altPt), neu))
                        }
                        onEntfernen={(pt) => stoppsBearbeiten((alt) => stoppEntfernen(alt, pt))}
                    />
                )}

                {/* Schreibfläche */}
                <div
                    ref={bodyRef}
                    contentEditable
                    suppressContentEditableWarning
                    // T-243: Die Rechtschreibprüfung macht der Browser (Edge →
                    // Windows-Rechtschreibprüfung). Hier wird sie nur
                    // ausdrücklich eingeschaltet, auf Deutsch — damit kein
                    // geerbtes spellcheck="false" sie abschaltet. Die Attribute
                    // stehen am Element selbst und gelangen nicht in getHtml().
                    // Mit eigener Prüfung (T-243) aus — sonst doppelte Linien.
                    spellCheck={!rs.aktiv}
                    lang="de"
                    role="textbox"
                    aria-multiline="true"
                    aria-label={ariaLabel}
                    // T-346: Tab bleibt im Text (Einzug/Tabulator) — Umschalt+Tab
                    // ohne etwas zum Zurücknehmen verlässt das Feld nach vorn.
                    aria-description="Tab rückt ein, Umschalt+Tab nimmt den Einzug zurück"
                    data-placeholder={placeholder}
                    className={pinsel ? "rte-body rte-pinsel" : "rte-body"}
                    style={{
                        flex: 1, overflowY: "auto",
                        padding: "16px 20px",
                        outline: "none",
                        // Editor-Schrift = Sende-Schrift (WYSIWYG): Times New Roman, 11pt.
                        fontSize: `${EDITOR_DEFAULT_SIZE_PT}pt`, lineHeight: 1.7,
                        color: "#000", background: "#fff",
                        fontFamily: EDITOR_DEFAULT_FONT_STACK,
                        ...(minHeight != null ? { minHeight } : null),
                        ...editorStyle,
                    }}
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onContextMenu={handleContextMenu}
                    onMouseDown={ausstehendVergessen}
                    onKeyUp={(e) => {
                        // Markieren per Tastatur (Umschalt+Pfeil) bedient den Pinsel ebenso.
                        if (pinsel && e.shiftKey) pinselAnwenden();
                    }}
                    onKeyDown={(e) => {
                        // Esc beendet den Pinsel — und NUR das: Das Verfassen-
                        // Fenster darf die Taste dann nicht als „Schließen" sehen.
                        if (pinsel && e.key === "Escape") {
                            e.preventDefault();
                            e.stopPropagation();
                            setPinsel(null);
                            return;
                        }
                        // Wer den Cursor bewegt, verlässt die Stelle, für die
                        // eine Schrift ohne Markierung gewählt wurde.
                        if (/^(Arrow|Home|End|Page)/.test(e.key)) ausstehendVergessen();
                        // Erst die Listen-Tasten, dann der Aufrufer: Das
                        // Verfassen-Fenster hängt hier seine eigenen Kürzel ein
                        // und soll ein bereits erledigtes Ereignis sehen.
                        if (listenTaste(e) && e.key === " ") {
                            // „1. " / „- " hat eben eine Liste angelegt.
                            listenpunkteAngleichen(bodyRef.current, {
                                fontFamily: ausstehendeSchriftRef.current ?? aktSchrift,
                                sizePt: ausstehendeGroesseRef.current ?? aktGroesse,
                            });
                        }
                        onKeyDown?.(e);
                    }}
                    onCompositionStart={(e) => {
                        komponiertRef.current = true;
                        onCompositionStart?.(e);
                    }}
                    onCompositionEnd={(e) => {
                        komponiertRef.current = false;
                        planeAusrichten();
                        onCompositionEnd?.(e);
                    }}
                />

                {/* Link-Popover — portaliert, damit es nicht am Karten-Overflow abgeschnitten wird. */}
                {linkOpen && linkPos && createPortal(
                    <>
                        <div
                            onMouseDown={() => setLinkOpen(false)}
                            style={{ position: "fixed", inset: 0, zIndex: 99_999 }}
                        />
                        <div
                            className="rte-linkpop"
                            style={{ position: "fixed", top: linkPos.top, left: linkPos.left, zIndex: 100_000 }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <input
                                autoFocus
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") { e.preventDefault(); applyLink(); }
                                    if (e.key === "Escape") { e.preventDefault(); setLinkOpen(false); }
                                }}
                                placeholder="https://…"
                                aria-label="Link-Adresse"
                                className="rte-linkpop-input"
                            />
                            <button type="button" onClick={applyLink} className="rte-linkpop-ok">
                                Einfügen
                            </button>
                        </div>
                    </>,
                    document.body,
                )}

                {/* Listen-Menü (Rechtsklick in eine Nummerierung, T-347) — portaliert wie das Link-Popover. */}
                {listenMenue && createPortal(
                    <>
                        <div
                            onMouseDown={listenMenueSchliessen}
                            onContextMenu={(e) => { e.preventDefault(); listenMenueSchliessen(); }}
                            style={{ position: "fixed", inset: 0, zIndex: 99_999 }}
                        />
                        <div
                            className="rte-listmenu"
                            role="menu"
                            aria-label="Nummerierung"
                            style={{ position: "fixed", top: listenMenue.top, left: listenMenue.left, zIndex: 100_000 }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                // Esc schließt nur das Menü — nicht das Verfassen-Fenster.
                                if (e.key === "Escape") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    listenMenueSchliessen();
                                }
                            }}
                        >
                            <div className="rte-listmenu-title">Nummerierung</div>
                            <div className="rte-listmenu-row">
                                <span>Beginnen bei</span>
                                <input
                                    autoFocus
                                    type="number"
                                    min={1}
                                    max={9999}
                                    value={startEingabe}
                                    onChange={(e) => setStartEingabe(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") { e.preventDefault(); startUebernehmen(); }
                                    }}
                                    aria-label="Nummerierung beginnen bei"
                                    className="rte-linkpop-input rte-listmenu-input"
                                />
                                <button
                                    type="button"
                                    className="rte-linkpop-ok"
                                    disabled={!startGueltig}
                                    onClick={startUebernehmen}
                                >
                                    Übernehmen
                                </button>
                            </div>
                            <button
                                type="button"
                                role="menuitem"
                                className="rte-listmenu-item"
                                disabled={listenMenue.fortsetzenAb == null}
                                onClick={fortsetzen}
                                title={listenMenue.fortsetzenAb == null
                                    ? "Darüber steht keine Nummerierung, die fortgesetzt werden könnte."
                                    : `Zählt die vorige Nummerierung weiter (ab ${listenMenue.fortsetzenAb}).`}
                            >
                                Nummerierung fortsetzen
                                {listenMenue.fortsetzenAb != null && (
                                    <span className="rte-listmenu-hint">ab {listenMenue.fortsetzenAb}</span>
                                )}
                            </button>
                            <div className="rte-listmenu-foot">
                                {/* Mit eigener Prüfung (T-243) hat das Browser-Menü keine Rechtschreibvorschläge. */}
                                {rs.aktiv ? "Umschalt+Rechtsklick: Browser-Menü" : "Umschalt+Rechtsklick: Browser-Menü (Rechtschreibung)"}
                            </div>
                        </div>
                    </>,
                    document.body,
                )}

                {/* T-243: Vorschläge der Rechtschreibprüfung (portaliert). */}
                {rs.popup}
            </div>
        );
    },
);
